import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

const EMPTY_FORM = {
     name: '',
     description: '',
     daily_rate: 650,
     quantity: 1,
     deposit_amount: 0,
     delivery_available: false,
     delivery_fee: 0,
     image_url: '',
};

export default function InventoryList() {
     const [equipment, setEquipment] = useState([]);
     const [loading, setLoading] = useState(true);
     const [showForm, setShowForm] = useState(false);
     const [editingId, setEditingId] = useState(null);
     const [form, setForm] = useState(EMPTY_FORM);
     const [uploading, setUploading] = useState(false);
     const [uploadError, setUploadError] = useState(null);
     const [saveError, setSaveError] = useState(null);
     const [saving, setSaving] = useState(false);

     useEffect(() => {
          fetchEquipment();
     }, []);

     async function fetchEquipment() {
          const { data } = await supabase
               .from('equipment')
               .select('*')
               .order('created_at');
          setEquipment(data || []);
          setLoading(false);
     }

     async function toggleActive(id, currentState) {
          await supabase
               .from('equipment')
               .update({ is_active: !currentState })
               .eq('id', id);
          fetchEquipment();
     }

     async function handleSave() {
          setSaveError(null);
          setSaving(true);

          const payload = {
               name: form.name,
               description: form.description,
               daily_rate: Math.round(Number(form.daily_rate) * 100),
               quantity: Number(form.quantity) || 1,
               deposit_amount: Math.round(Number(form.deposit_amount) * 100),
               delivery_available: form.delivery_available,
               delivery_fee: Math.round(Number(form.delivery_fee) * 100),
               image_url: form.image_url || null,
          };

          const { data, error } = editingId
               ? await supabase
                      .from('equipment')
                      .update(payload)
                      .eq('id', editingId)
                      .select()
               : await supabase
                      .from('equipment')
                      .insert({ ...payload, is_active: true })
                      .select();

          setSaving(false);

          if (error) {
               setSaveError(error.message || 'Save failed.');
               return;
          }
          if (editingId && (!data || data.length === 0)) {
               setSaveError(
                    'No row was updated. Your admin session may have expired — try signing out and back in.'
               );
               return;
          }

          setForm(EMPTY_FORM);
          setShowForm(false);
          setEditingId(null);
          fetchEquipment();
     }

     async function handleImageUpload(e) {
          const file = e.target.files?.[0];
          if (!file) return;
          setUploadError(null);

          if (!file.type.startsWith('image/')) {
               setUploadError('Please select an image file.');
               return;
          }
          if (file.size > 5 * 1024 * 1024) {
               setUploadError('Image must be under 5MB.');
               return;
          }

          setUploading(true);
          const ext = file.name.split('.').pop();
          const path = `${crypto.randomUUID()}.${ext}`;
          const { error: upErr } = await supabase.storage
               .from('equipment-images')
               .upload(path, file, { cacheControl: '3600', upsert: false });

          if (upErr) {
               setUploadError(upErr.message);
               setUploading(false);
               return;
          }

          const { data: pub } = supabase.storage
               .from('equipment-images')
               .getPublicUrl(path);
          setForm((prev) => ({ ...prev, image_url: pub.publicUrl }));
          setUploading(false);
     }

     function clearImage() {
          setForm((prev) => ({ ...prev, image_url: '' }));
          setUploadError(null);
     }

     async function handleDelete(item) {
          if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
          await supabase.from('equipment').delete().eq('id', item.id);
          fetchEquipment();
     }

     function startEdit(item) {
          setForm({
               name: item.name,
               description: item.description || '',
               daily_rate: item.daily_rate / 100,
               quantity: item.quantity || 1,
               deposit_amount: (item.deposit_amount || 0) / 100,
               delivery_available: item.delivery_available || false,
               delivery_fee: (item.delivery_fee || 0) / 100,
               image_url: item.image_url || '',
          });
          setEditingId(item.id);
          setShowForm(true);
     }

     const inputClasses =
          'w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-darker placeholder-darker/30 focus:outline-none focus:border-yellow duration-300';

     if (loading) {
          return <p className='text-darker/60'>Loading inventory...</p>;
     }

     return (
          <div>
               <div className='flex items-center justify-between mb-6'>
                    <h1 className='text-darker text-2xl font-bold'>Inventory</h1>
                    <button
                         onClick={() => {
                              setForm(EMPTY_FORM);
                              setEditingId(null);
                              setUploadError(null);
                              setSaveError(null);
                              setShowForm(!showForm);
                         }}
                         className='px-4 py-2 rounded-lg text-sm border border-yellow text-yellow hover:bg-yellow/10 duration-300'
                    >
                         {showForm ? 'Cancel' : '+ Add Equipment'}
                    </button>
               </div>

               {/* Add/Edit Form */}
               {showForm && (
                    <div className='bg-white rounded-xl p-5 border border-gray-200 mb-6 space-y-4'>
                         <h3 className='text-yellow text-sm uppercase tracking-wider'>
                              {editingId ? 'Edit Equipment' : 'Add Equipment'}
                         </h3>
                         <div>
                              <label className='block text-darker/60 text-sm mb-1'>Name</label>
                              <input
                                   type='text'
                                   value={form.name}
                                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                                   placeholder='e.g., Trailer Unit A'
                                   className={inputClasses}
                              />
                         </div>
                         <div>
                              <label className='block text-darker/60 text-sm mb-1'>
                                   Description
                              </label>
                              <textarea
                                   value={form.description}
                                   onChange={(e) =>
                                        setForm({ ...form, description: e.target.value })
                                   }
                                   placeholder='Full dustless tile removal package'
                                   rows={3}
                                   className={inputClasses}
                              />
                         </div>
                         <div>
                              <label className='block text-darker/60 text-sm mb-1'>
                                   Image
                              </label>
                              {form.image_url ? (
                                   <div className='flex items-start gap-4'>
                                        <img
                                             src={form.image_url}
                                             alt='Equipment preview'
                                             className='w-32 h-32 object-cover rounded-lg border border-gray-200'
                                        />
                                        <div className='flex flex-col gap-2'>
                                             <label className='px-3 py-1.5 rounded-lg text-xs text-darker/60 border border-gray-200 hover:border-gray-200er/40 hover:text-darker duration-300 cursor-pointer text-center'>
                                                  Replace
                                                  <input
                                                       type='file'
                                                       accept='image/*'
                                                       onChange={handleImageUpload}
                                                       className='hidden'
                                                       disabled={uploading}
                                                  />
                                             </label>
                                             <button
                                                  type='button'
                                                  onClick={clearImage}
                                                  className='px-3 py-1.5 rounded-lg text-xs text-red-400 border border-red-500/30 hover:bg-red-500/10 duration-300'
                                             >
                                                  Remove
                                             </button>
                                        </div>
                                   </div>
                              ) : (
                                   <label className='inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-yellow text-yellow hover:bg-yellow/10 duration-300 cursor-pointer'>
                                        {uploading ? 'Uploading...' : '+ Upload Image'}
                                        <input
                                             type='file'
                                             accept='image/*'
                                             onChange={handleImageUpload}
                                             className='hidden'
                                             disabled={uploading}
                                        />
                                   </label>
                              )}
                              {uploadError && (
                                   <p className='text-red-400 text-xs mt-2'>{uploadError}</p>
                              )}
                         </div>
                         <div className='grid grid-cols-2 gap-4'>
                              <div>
                                   <label className='block text-darker/60 text-sm mb-1'>
                                        Daily Rate ($)
                                   </label>
                                   <input
                                        type='number'
                                        value={form.daily_rate}
                                        onChange={(e) =>
                                             setForm({ ...form, daily_rate: Number(e.target.value) })
                                        }
                                        className={inputClasses}
                                   />
                              </div>
                              <div>
                                   <label className='block text-darker/60 text-sm mb-1'>
                                        Available Quantity
                                   </label>
                                   <input
                                        type='number'
                                        min='0'
                                        value={form.quantity}
                                        onChange={(e) =>
                                             setForm({ ...form, quantity: Number(e.target.value) })
                                        }
                                        className={inputClasses}
                                   />
                              </div>
                         </div>
                         <div>
                              <label className='block text-darker/60 text-sm mb-1'>
                                   Security Deposit ($) — refunded on return
                              </label>
                              <input
                                   type='number'
                                   min='0'
                                   step='0.01'
                                   value={form.deposit_amount}
                                   onChange={(e) =>
                                        setForm({ ...form, deposit_amount: Number(e.target.value) })
                                   }
                                   placeholder='500'
                                   className={inputClasses + ' max-w-[200px]'}
                              />
                         </div>
                         {/* Delivery Options */}
                         <div className='border-t border-gray-200 pt-4'>
                              <label className='flex items-center gap-3 cursor-pointer'>
                                   <input
                                        type='checkbox'
                                        checked={form.delivery_available}
                                        onChange={(e) =>
                                             setForm({ ...form, delivery_available: e.target.checked })
                                        }
                                        className='w-5 h-5 rounded border-gray-200 accent-yellow'
                                   />
                                   <span className='text-darker'>Delivery available for this item</span>
                              </label>
                              {form.delivery_available && (
                                   <div className='mt-3 ml-8'>
                                        <label className='block text-darker/60 text-sm mb-1'>
                                             Delivery Fee ($)
                                        </label>
                                        <input
                                             type='number'
                                             min='0'
                                             step='0.01'
                                             value={form.delivery_fee}
                                             onChange={(e) =>
                                                  setForm({ ...form, delivery_fee: Number(e.target.value) })
                                             }
                                             placeholder='150'
                                             className={inputClasses + ' max-w-[200px]'}
                                        />
                                   </div>
                              )}
                         </div>
                         <div className='flex items-center gap-4'>
                              <button
                                   onClick={handleSave}
                                   disabled={!form.name || saving}
                                   className='px-6 py-2 rounded-lg text-sm border border-yellow text-yellow hover:bg-yellow/10 duration-300 disabled:opacity-50'
                              >
                                   {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Equipment'}
                              </button>
                              {saveError && (
                                   <p className='text-red-400 text-sm'>{saveError}</p>
                              )}
                         </div>
                    </div>
               )}

               {/* Equipment List */}
               <div className='space-y-3'>
                    {equipment.length === 0 ? (
                         <div className='bg-white rounded-xl p-5 border border-gray-200 text-darker/40'>
                              No equipment added yet. Click "+ Add Equipment" to get started.
                         </div>
                    ) : (
                         equipment.map((item) => (
                              <div
                                   key={item.id}
                                   className='bg-white rounded-xl p-5 border border-gray-200 flex flex-col tablet:flex-row items-start tablet:items-center justify-between gap-4'
                              >
                                   <div className='flex items-start gap-4 flex-1'>
                                        {item.image_url && (
                                             <img
                                                  src={item.image_url}
                                                  alt={item.name}
                                                  className='w-20 h-20 object-cover rounded-lg border border-gray-200 flex-shrink-0'
                                             />
                                        )}
                                        <div>
                                        <div className='flex items-center gap-2'>
                                             <h3 className='text-darker text-lg'>{item.name}</h3>
                                             <span
                                                  className={`px-2 py-0.5 rounded text-xs ${
                                                       item.is_active
                                                            ? 'bg-green-500/20 text-green-300'
                                                            : 'bg-red-500/20 text-red-300'
                                                  }`}
                                             >
                                                  {item.is_active ? 'Active' : 'Inactive'}
                                             </span>
                                        </div>
                                        {item.description && (
                                             <p className='text-darker/40 text-sm mt-1'>
                                                  {item.description}
                                             </p>
                                        )}
                                        <p className='text-darker/40 text-sm mt-1'>
                                             <span className='text-yellow'>${(item.daily_rate / 100).toLocaleString()}/day</span>
                                             <span className='ml-3'>Qty: {item.quantity ?? 1}</span>
                                             {(item.deposit_amount || 0) > 0 && (
                                                  <span className='ml-3'>
                                                       Deposit: ${(item.deposit_amount / 100).toLocaleString()}
                                                  </span>
                                             )}
                                             {item.delivery_available && (
                                                  <span className='ml-3 text-green-300'>
                                                       Delivery: ${(item.delivery_fee / 100).toLocaleString()}
                                                  </span>
                                             )}
                                        </p>
                                        </div>
                                   </div>
                                   <div className='flex gap-2'>
                                        <button
                                             onClick={() => startEdit(item)}
                                             className='px-3 py-1.5 rounded-lg text-xs text-darker/60 border border-gray-200 hover:border-gray-200er/40 hover:text-darker duration-300'
                                        >
                                             Edit
                                        </button>
                                        <button
                                             onClick={() => toggleActive(item.id, item.is_active)}
                                             className={`px-3 py-1.5 rounded-lg text-xs border duration-300 ${
                                                  item.is_active
                                                       ? 'text-red-700 border-red-500/30 hover:bg-red-500/10'
                                                       : 'text-green-300 border-green-500/30 hover:bg-green-500/10'
                                             }`}
                                        >
                                             {item.is_active ? 'Deactivate' : 'Activate'}
                                        </button>
                                        <button
                                             onClick={() => handleDelete(item)}
                                             className='px-3 py-1.5 rounded-lg text-xs text-red-400 border border-red-500/30 hover:bg-red-500/10 duration-300'
                                        >
                                             Delete
                                        </button>
                                   </div>
                              </div>
                         ))
                    )}
               </div>
          </div>
     );
}
